import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import AtomicButton from '../../atomic/button/Button';
import InputBox from '../../atomic/inputbox/InputBox';
import ImageUploader from '../../composed/products/ImageUpLoader';
import { MenuProps, sharedInputSx } from '../Products/kitchenProductsConstants';

const AD_TYPE_OPTIONS = [
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
];

const AD_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
];

const inputSx = { width: '100%', height: 'auto', margin: '0 0 8px' };

const selectControlSx = {
  width: '100%',
  ...sharedInputSx,
  '& .MuiSelect-select': {
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
};

const defaultAdForm = () => ({
  ad_title: '',
  ad_type: 'image',
  category: 'food',
  status: 'draft',
  tags: '',
  good_to_use_on: 'facebook, instagram',
});

const defaultToolForm = () => ({
  tool_name: '',
  website_url: '',
  short_description: '',
  category: 'AI Tool',
  status: true,
  featured: false,
  notes: '',
});

export function AdLibraryAddModal({ open, activeTab, onClose, onSubmit, submitting }) {
  const isToolTab = activeTab === 'tools';
  const [adForm, setAdForm] = useState(defaultAdForm);
  const [toolForm, setToolForm] = useState(defaultToolForm);
  const [imageFile, setImageFile] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setAdForm(defaultAdForm());
      setToolForm(defaultToolForm());
      setImageFile('');
      setPreviewUrl('');
      setError('');
    }
  }, [open, activeTab]);

  const handleAdChange = (field) => (event) => {
    setAdForm((prev) => ({ ...prev, [field]: event.target.value }));
    setError('');
  };

  const handleToolChange = (field) => (event) => {
    const value =
      event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setToolForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmitAd = () => {
    if (!adForm.ad_title.trim()) {
      setError('Ad title is required.');
      return;
    }
    if (!imageFile) {
      setError('Select an image.');
      return;
    }
    if (!['video', 'image'].includes(adForm.ad_type)) {
      setError('Ad type must be video or image.');
      return;
    }
    if (!adForm.category.trim()) {
      setError('Category is required.');
      return;
    }

    const tags = adForm.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    const good_to_use_on = adForm.good_to_use_on
      .split(',')
      .map((platform) => platform.trim().toLowerCase())
      .filter(Boolean);

    onSubmit({
      kind: 'ad',
      ad_title: adForm.ad_title.trim(),
      ad_type: adForm.ad_type,
      category: adForm.category.trim().toLowerCase(),
      status: adForm.status,
      tags,
      good_to_use_on,
      imageFile: imageFile || undefined,
    });
  };

  const handleSubmitTool = () => {
    if (!toolForm.tool_name.trim()) {
      setError('Tool name is required.');
      return;
    }
    if (!toolForm.website_url.trim()) {
      setError('Website URL is required.');
      return;
    }

    onSubmit({
      kind: 'tool',
      tool_name: toolForm.tool_name.trim(),
      website_url: toolForm.website_url.trim(),
      short_description: toolForm.short_description.trim(),
      category: toolForm.category.trim() || 'AI Tool',
      status: toolForm.status,
      featured: toolForm.featured,
      notes: toolForm.notes.trim(),
    });
  };

  const handleSubmit = () => {
    if (isToolTab) handleSubmitTool();
    else handleSubmitAd();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      fullWidth
      maxWidth={isToolTab ? 'sm' : 'md'}
      className="ad-library-modal"
      PaperProps={{
        className: `ad-library-modal__paper ${
          isToolTab ? 'ad-library-modal__paper--tool' : 'ad-library-modal__paper--visual'
        }`,
      }}
    >
      <div className="ad-library-modal__header">
        <Typography className="ad-library-modal__title">
          {isToolTab ? 'Add Tool' : 'Add Visual'}
        </Typography>
        <Typography className="ad-library-modal__subtitle">
          {isToolTab
            ? 'Add a marketing tool to your branch library.'
            : 'Upload a creative and fill in the ad details below.'}
        </Typography>
      </div>

      <Divider className="ad-library-modal__divider" />

      <div className="ad-library-modal__body">
        {isToolTab ? (
          <Box className="ad-library-modal__form">
            <InputBox
              type="text"
              label="Tool name"
              name="tool_name"
              value={toolForm.tool_name}
              onChange={handleToolChange('tool_name')}
              sx={inputSx}
            />

            <InputBox
              type="text"
              label="Website URL"
              name="website_url"
              value={toolForm.website_url}
              onChange={handleToolChange('website_url')}
              sx={inputSx}
            />

            <InputBox
              type="text"
              label="Short description"
              name="short_description"
              value={toolForm.short_description}
              onChange={handleToolChange('short_description')}
              multiline
              minRows={3}
              sx={inputSx}
            />

            <Box className="ad-library-modal__tool-meta">
              <Box className="ad-library-modal__tool-meta-left">
                <InputBox
                  type="text"
                  label="Category"
                  name="category"
                  value={toolForm.category}
                  onChange={handleToolChange('category')}
                  inputErrorMsg="e.g. AI Tool"
                  sx={inputSx}
                />

                <Box className="ad-library-modal__checks">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={toolForm.status}
                        onChange={handleToolChange('status')}
                        sx={{ color: '#024CAA', '&.Mui-checked': { color: '#024CAA' } }}
                      />
                    }
                    label="Active"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={toolForm.featured}
                        onChange={handleToolChange('featured')}
                        sx={{ color: '#024CAA', '&.Mui-checked': { color: '#024CAA' } }}
                      />
                    }
                    label="Featured"
                  />
                </Box>
              </Box>

              <InputBox
                type="text"
                label="Notes"
                name="notes"
                value={toolForm.notes}
                onChange={handleToolChange('notes')}
                multiline
                sx={{
                  ...inputSx,
                  margin: 0,
                  height: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: '100%',
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-inputMultiline': {
                    height: '100% !important',
                    minHeight: '100% !important',
                    boxSizing: 'border-box',
                  },
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box className="ad-library-modal__ad-layout">
            <div className="ad-library-modal__image-box">
              <div className="ad-library-modal__image-uploader">
                <ImageUploader
                  onChange={(file) => setImageFile(file)}
                  imageFile={imageFile}
                  setImageFile={setImageFile}
                  previewUrl={previewUrl}
                  setPreviewUrl={setPreviewUrl}
                />
              </div>
              <Typography className="ad-library-modal__image-hint">
                Image upload API will connect this file later.
              </Typography>
            </div>

            <Box className="ad-library-modal__form">
              <InputBox
                type="text"
                label="Ad title"
                name="ad_title"
                value={adForm.ad_title}
                onChange={handleAdChange('ad_title')}
                sx={inputSx}
              />

              <Box className="ad-library-modal__row">
                <FormControl size="small" sx={selectControlSx}>
                  <InputLabel id="ad-library-ad-type-label">Ad type</InputLabel>
                  <Select
                    labelId="ad-library-ad-type-label"
                    id="ad-library-ad-type"
                    value={adForm.ad_type}
                    onChange={handleAdChange('ad_type')}
                    input={<OutlinedInput label="Ad type" />}
                    MenuProps={MenuProps}
                  >
                    {AD_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={selectControlSx}>
                  <InputLabel id="ad-library-status-label">Status</InputLabel>
                  <Select
                    labelId="ad-library-status-label"
                    id="ad-library-status"
                    value={adForm.status}
                    onChange={handleAdChange('status')}
                    label="Status"
                    MenuProps={MenuProps}
                  >
                    {AD_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <InputBox
                type="text"
                label="Category"
                name="category"
                value={adForm.category}
                onChange={handleAdChange('category')}
                inputErrorMsg="e.g. food"
                sx={inputSx}
              />

              <InputBox
                type="text"
                label="Platforms (comma separated)"
                name="good_to_use_on"
                value={adForm.good_to_use_on}
                onChange={handleAdChange('good_to_use_on')}
                inputErrorMsg="facebook, instagram"
                sx={inputSx}
              />

              <InputBox
                type="text"
                label="Tags (comma separated)"
                name="tags"
                value={adForm.tags}
                onChange={handleAdChange('tags')}
                sx={inputSx}
              />
            </Box>
          </Box>
        )}

        {error ? (
          <Typography className="ad-library-modal__error">{error}</Typography>
        ) : null}
      </div>

      <Divider className="ad-library-modal__divider" />

      <div className="ad-library-modal__footer">
        <AtomicButton
          label="CANCEL"
          variant="outlined"
          handleSubmit={onClose}
          disabled={submitting}
          style={{
            borderRadius: '5px',
            width: '120px',
            height: '42px',
            fontWeight: '600',
            letterSpacing: '0.3px',
            marginTop: 0,
            marginBottom: 0,
            backgroundColor: '#ffffff',
            color: '#091057',
            border: '1px solid #d1d5db',
          }}
        />
        <AtomicButton
          label={isToolTab ? 'ADD TOOL' : 'ADD VISUAL'}
          handleSubmit={handleSubmit}
          loading={submitting}
          disabled={submitting}
          style={{
            borderRadius: '5px',
            width: '150px',
            height: '42px',
            fontWeight: '600',
            letterSpacing: '0.3px',
            marginTop: 0,
            marginBottom: 0,
          }}
        />
      </div>
    </Dialog>
  );
}
